/**
 * WordPress dependencies
 */
import {
	BaseControl,
	Grid,
	parseQuantityAndUnitFromRawValue,
} from '@safe-wordpress/components';
import { useInstanceId } from '@safe-wordpress/compose';
import { useState } from '@safe-wordpress/element';
import { _x } from '@safe-wordpress/i18n';
import warning from '@safe-wordpress/warning';

/**
 * Internal dependencies
 */
import LinkedButton from './linked-button';
import InputControl from './input-control';
import {
	InputWrapper,
	ResetButton,
	LinkedButtonWrapper,
} from './styles/box-control-styles';
import {
	DEFAULT_VALUES,
	getInitialSide,
	isValueMixed,
	isValuesDefined,
	getAllowedSides,
} from './utils';
import { useControlledState } from '../utils';
import type {
	BoxControlIconProps,
	BoxControlProps,
	BoxControlValue,
} from './types';

const defaultInputProps = {
	min: 0,
};

const noop = () => {};

function useUniqueId( idProp?: string ) {
	const instanceId = useInstanceId( BoxControl, 'inspector-box-control' );

	return idProp || instanceId;
}

/**
 * A control that lets users set values for top, right, bottom, and left. Can be
 * used as an input control for values like `padding` or `margin`.
 *
 * ```jsx
 * import { useState } from 'react';
 * import { BoxControl } from '@safe-wordpress/components';
 *
 * function Example() {
 *   const [ values, setValues ] = useState( {
 *     top: '50px',
 *     left: '10%',
 *     right: '10%',
 *     bottom: '50px',
 *   } );
 *
 *   return (
 *     <BoxControl
 *       __next40pxDefaultSize
 *       values={ values }
 *       onChange={ setValues }
 *     />
 *   );
 * };
 * ```
 * @param root0
 * @param root0.__next40pxDefaultSize
 * @param root0.id
 * @param root0.inputProps
 * @param root0.onChange
 * @param root0.label
 * @param root0.values
 * @param root0.units
 * @param root0.sides
 * @param root0.splitOnAxis
 * @param root0.allowReset
 * @param root0.resetValues
 * @param root0.presets
 * @param root0.presetKey
 * @param root0.onMouseOver
 * @param root0.onMouseOut
 * @param root0.disabledSides
 * @param root0.showAllSides
 */
export function BoxControl( {
	__next40pxDefaultSize = false,
	id: idProp,
	inputProps = defaultInputProps,
	onChange = noop,
	label = _x( 'Box Control', 'text', 'nelio-ab-testing' ),
	values: valuesProp,
	units,
	sides,
	disabledSides,
	showAllSides = false,
	splitOnAxis = false,
	allowReset = true,
	resetValues = DEFAULT_VALUES,
	presets,
	presetKey,
	onMouseOver,
	onMouseOut,
}: BoxControlProps ): JSX.Element {
	const [ values, setValues ] = useControlledState( valuesProp, {
		initial: undefined,
		fallback: DEFAULT_VALUES,
	} );
	const inputValues = values || DEFAULT_VALUES;
	const hasInitialValue = isValuesDefined( valuesProp );
	const hasOneSide = sides?.length === 1;

	const [ isDirty, setIsDirty ] = useState( hasInitialValue );
	const [ isLinked, setIsLinked ] = useState(
		! hasInitialValue || ! isValueMixed( inputValues ) || hasOneSide
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [ side, setSide ] = useState< BoxControlIconProps[ 'side' ] >(
		getInitialSide( isLinked, splitOnAxis )
	);

	// Tracking selected units via internal state allows filtering of CSS unit
	// only values from being saved while maintaining preexisting unit selection
	// behaviour. Filtering CSS only values prevents invalid style values.
	const [ selectedUnits, setSelectedUnits ] = useState< BoxControlValue >( {
		top: parseQuantityAndUnitFromRawValue( valuesProp?.top )[ 1 ] || 'px',
		right:
			parseQuantityAndUnitFromRawValue( valuesProp?.right )[ 1 ] || 'px',
		bottom:
			parseQuantityAndUnitFromRawValue( valuesProp?.bottom )[ 1 ] || 'px',
		left: parseQuantityAndUnitFromRawValue( valuesProp?.left )[ 1 ] || 'px',
	} );

	const id = useUniqueId( idProp );
	const headingId = `${ id }-heading`;

	const toggleLinked = () => {
		setIsLinked( ! isLinked );
		setSide( getInitialSide( ! isLinked, splitOnAxis ) );
	};

	const handleOnFocus = (
		_event: React.FocusEvent< HTMLInputElement >,
		{ side: nextSide }: { side: typeof side }
	) => {
		setSide( nextSide );
	};

	const handleOnChange = ( nextValues: BoxControlValue ) => {
		nextValues = {
			...nextValues,
			top:
				nextValues.top && /^[0-9.]+$/.test( nextValues.top )
					? nextValues.top + selectedUnits.top
					: nextValues.top,
			bottom:
				nextValues.bottom && /^[0-9.]+$/.test( nextValues.bottom )
					? nextValues.bottom + selectedUnits.bottom
					: nextValues.bottom,
			left:
				nextValues.left && /^[0-9.]+$/.test( nextValues.left )
					? nextValues.left + selectedUnits.left
					: nextValues.left,
			right:
				nextValues.right && /^[0-9.]+$/.test( nextValues.right )
					? nextValues.right + selectedUnits.right
					: nextValues.right,
		};
		onChange( nextValues );
		setValues( nextValues );
		setIsDirty( true );
	};

	const handleOnReset = () => {
		onChange( resetValues );
		setValues( resetValues );
		setSelectedUnits( resetValues );
		setIsDirty( false );
	};

	const inputControlProps = {
		onMouseOver,
		onMouseOut,
		...inputProps,
		onChange: handleOnChange,
		onFocus: handleOnFocus,
		isLinked,
		units,
		selectedUnits,
		setSelectedUnits,
		sides,
		disabledSides,
		values: inputValues,
		__next40pxDefaultSize,
		presets,
		presetKey,
	};

	const sidesToRender = getAllowedSides( sides );

	if ( ( presets && ! presetKey ) || ( ! presets && presetKey ) ) {
		const definedProp = presets ? 'presets' : 'presetKey';
		const missingProp = presets ? 'presetKey' : 'presets';
		warning(
			`wp.components.BoxControl: the '${ missingProp }' prop is required when the '${ definedProp }' prop is defined.`
		);
	}

	return (
		<Grid
			id={ id }
			columns={ 3 }
			templateColumns="1fr min-content min-content"
			role="group"
			aria-labelledby={ headingId }
		>
			<BaseControl.VisualLabel id={ headingId }>
				{ label }
			</BaseControl.VisualLabel>
			{ ! showAllSides && isLinked && (
				<InputWrapper>
					<InputControl side="all" { ...inputControlProps } />
				</InputWrapper>
			) }
			{ ! showAllSides && ! hasOneSide && (
				<LinkedButtonWrapper>
					<LinkedButton
						onClick={ toggleLinked }
						isLinked={ isLinked }
					/>
				</LinkedButtonWrapper>
			) }

			{ ! showAllSides &&
				! isLinked &&
				splitOnAxis &&
				[ 'vertical', 'horizontal' ].map( ( axis ) => (
					<InputControl
						key={ axis }
						side={ axis as 'horizontal' | 'vertical' }
						{ ...inputControlProps }
					/>
				) ) }
			{ ( showAllSides || ( ! isLinked && ! splitOnAxis ) ) &&
				Array.from( sidesToRender ).map( ( axis ) => (
					<InputControl
						key={ axis }
						side={ axis }
						{ ...inputControlProps }
					/>
				) ) }
			{ allowReset && (
				<ResetButton
					className="component-box-control__reset-button"
					variant="secondary"
					size="small"
					onClick={ handleOnReset }
					disabled={ ! isDirty }
				>
					{ _x( 'Reset', 'command', 'nelio-ab-testing' ) }
				</ResetButton>
			) }
		</Grid>
	);
}

export { applyValueToSides } from './utils';
export type { BoxControlValue } from './types';
